import { useApolloClient } from '@apollo/client'
import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import { useState } from 'react'
import DialogEndProject from '../../controls/DialogEndProject'
import { useWavesRefetchQueries } from '../../hooks/useWavesRefetchQueries'
import { usePauseResumeSurvey } from './usePauseResumeSurvey'

interface Props {
  isOpen: boolean
  toggleIsOpen: (open: boolean) => void
  surveyId: string
}

/**
 * End one market of a multi-market project. `EndProject` is hard-wired to
 * `selectedProject.surveys[0]` and navigates to the single-market results
 * route afterwards, so this ends the given market's survey in place and
 * refetches the queries that display its state.
 */
const EndMarket = ({ isOpen, toggleIsOpen, surveyId }: Props) => {
  const client = useApolloClient()
  const { enqueueSnackbar } = useSnackbar()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const wavesRefetchQueries = useWavesRefetchQueries()
  const { end } = usePauseResumeSurvey(() =>
    client
      .refetchQueries({
        include: [
          'projectBySurveyId',
          'projectById',
          'surveyOrder',
          ...wavesRefetchQueries
        ]
      })
      // Refreshing the cache is best-effort: `surveyOrder` rejects for surveys
      // that never had an order placed.
      .catch(() => undefined)
  )

  // The dialog stays open (button busy) until the mutations settle, then
  // failures surface as a snackbar — closing immediately would drop them
  // silently.
  const handleConfirm = async () => {
    setIsSubmitting(true)
    const succeeded = await end(surveyId)
    setIsSubmitting(false)
    toggleIsOpen(false)
    if (!succeeded) {
      enqueueSnackbar('Could not end the market. Please try again.', {
        variant: SnackbarVariant.Error
      })
    }
  }

  return (
    <DialogEndProject
      entity="market"
      isOpen={isOpen}
      isSubmitting={isSubmitting}
      toggleOpenDialog={toggleIsOpen}
      onEndProject={() => {
        void handleConfirm()
      }}
    />
  )
}

export default EndMarket
